/**
 * User-defined reporting ratios (localStorage). Recruitment KPIs + optional prompt placeholders.
 */

import type { RecruitmentDashboardPayload } from '../recruitmentDashboard.types';

export type ReportingRatioFormat = 'normalised' | 'percentage';

export type ReportingRatioDefinition = {
  id: string;
  name: string;
  numeratorMetricId: string;
  denominatorMetricId: string;
  format: ReportingRatioFormat;
};

const STORAGE_KEY = (tenantId: string | null | undefined) =>
  `werftools_reporting_ratios_v1_${tenantId || 'global'}`;

export const REPORTING_RATIO_METRICS: { id: string; label: string; group: 'recruitment' | 'prompts' }[] = [
  { id: 'jobsAdded', label: 'Jobs Added', group: 'recruitment' },
  { id: 'shortlistsAdded', label: 'Shortlists Added', group: 'recruitment' },
  { id: 'cvsSent', label: 'CVs Sent', group: 'recruitment' },
  { id: 'firstInterviews', label: '1st Interviews', group: 'recruitment' },
  { id: 'furtherInterviews', label: 'Further Interviews', group: 'recruitment' },
  { id: 'totalInterviews', label: 'Total Interviews', group: 'recruitment' },
  { id: 'offers', label: 'Offers', group: 'recruitment' },
  { id: 'placements', label: 'Placements', group: 'recruitment' },
  { id: 'permPlacements', label: 'Perm Placements', group: 'recruitment' },
  { id: 'promptsTotal', label: 'Total Prompts Generated', group: 'prompts' },
  { id: 'promptsUser', label: 'User Generated Prompts Generated', group: 'prompts' },
  { id: 'promptsAutomation', label: 'Automation Prompts Generated', group: 'prompts' },
  { id: 'promptsDefault', label: 'Default Prompts Generated', group: 'prompts' },
  { id: 'promptsCustom', label: 'Custom Prompts Generated', group: 'prompts' },
];

const DEFAULT_ROWS: ReportingRatioDefinition[] = [
  {
    id: 'd1',
    name: 'Jobs Added to CVs Sent',
    numeratorMetricId: 'jobsAdded',
    denominatorMetricId: 'cvsSent',
    format: 'normalised',
  },
  {
    id: 'd2',
    name: 'CVs Sent to Offers',
    numeratorMetricId: 'cvsSent',
    denominatorMetricId: 'offers',
    format: 'normalised',
  },
  {
    id: 'd3',
    name: 'Offers to Placements',
    numeratorMetricId: 'offers',
    denominatorMetricId: 'placements',
    format: 'normalised',
  },
  {
    id: 'd4',
    name: 'CVs Sent to 1st Interviews',
    numeratorMetricId: 'cvsSent',
    denominatorMetricId: 'firstInterviews',
    format: 'normalised',
  },
  {
    id: 'd5',
    name: '1st Interviews to Offers',
    numeratorMetricId: 'firstInterviews',
    denominatorMetricId: 'offers',
    format: 'normalised',
  },
];

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function loadReportingRatios(tenantId: string | null | undefined): ReportingRatioDefinition[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(tenantId));
    if (raw) {
      const arr = JSON.parse(raw) as ReportingRatioDefinition[];
      if (Array.isArray(arr) && arr.length) return arr;
    }
  } catch {
    /* ignore */
  }
  const seeded = DEFAULT_ROWS.map((r) => ({ ...r, id: genId() }));
  saveReportingRatios(tenantId, seeded);
  return seeded;
}

export function saveReportingRatios(
  tenantId: string | null | undefined,
  rows: ReportingRatioDefinition[]
): void {
  localStorage.setItem(STORAGE_KEY(tenantId), JSON.stringify(rows));
  window.dispatchEvent(new CustomEvent('werftools-reporting-ratios-saved'));
}

export function getKpiMetric(
  id: string,
  kpis: RecruitmentDashboardPayload['kpis'],
  promptExtras?: Partial<Record<string, number>>
): number {
  const p = promptExtras || {};
  switch (id) {
    case 'jobsAdded':
      return kpis.jobsAdded;
    case 'shortlistsAdded':
      return kpis.shortlistsAdded;
    case 'cvsSent':
      return kpis.cvsSent;
    case 'firstInterviews':
      return kpis.firstInterviews;
    case 'furtherInterviews':
      return kpis.furtherInterviews;
    case 'totalInterviews':
      return kpis.firstInterviews + kpis.furtherInterviews;
    case 'offers':
      return kpis.offers;
    case 'placements':
    case 'permPlacements':
      return kpis.placements;
    case 'promptsTotal':
      return p.promptsTotal ?? 0;
    case 'promptsUser':
      return p.promptsUser ?? 0;
    case 'promptsAutomation':
      return p.promptsAutomation ?? 0;
    case 'promptsDefault':
      return p.promptsDefault ?? 0;
    case 'promptsCustom':
      return p.promptsCustom ?? 0;
    default:
      return 0;
  }
}

/** Normalised = 1 : (den/num); percentage = num/den * 100 (e.g. share of prompts). */
export function computeRatioDisplay(
  row: ReportingRatioDefinition,
  kpis: RecruitmentDashboardPayload['kpis'],
  promptExtras?: Partial<Record<string, number>>
): string {
  const num = getKpiMetric(row.numeratorMetricId, kpis, promptExtras);
  const den = getKpiMetric(row.denominatorMetricId, kpis, promptExtras);
  if (row.format === 'percentage') {
    if (den <= 0) return '—';
    return `${Math.round((num / den) * 1000) / 10}%`;
  }
  if (num <= 0) return '—';
  const x = den / num;
  const rounded = Math.round(x * 10) / 10;
  return `1:${rounded}`;
}
